// All-time fun stats. Port of scripts/analysis/fun_stats.py from the Fantasy Points Data Database.
// Pure data-in/data-out — no platform deps, no hardcoded owners/eras/nicknames.
// Consumes the normalized matchup schema (RECAP_FEATURE_BRIEF.md §5).

function mean(arr) {
  if (!arr.length) return 0;
  let s = 0;
  for (const v of arr) s += v;
  return s / arr.length;
}

function stdev(arr) {
  if (arr.length < 2) return 0;
  const m = mean(arr);
  let v = 0;
  for (const x of arr) v += (x - m) * (x - m);
  return Math.sqrt(v / (arr.length - 1));
}

function detectEras(years, override) {
  if (override && override.length) return override;
  if (!years.length) return [];
  const ys = years.map(Number).sort((a, b) => a - b);
  const first = ys[0];
  const last = ys[ys.length - 1];
  const span = last - first + 1;
  if (span < 6) {
    return [{ name: `All-Time (${first}-${last})`, startYear: first, endYear: last }];
  }
  const third = Math.floor(span / 3);
  const e1End = first + third - 1;
  const e2End = first + 2 * third - 1;
  return [
    { name: `Early (${first}-${e1End})`, startYear: first, endYear: e1End },
    { name: `Middle (${e1End + 1}-${e2End})`, startYear: e1End + 1, endYear: e2End },
    { name: `Recent (${e2End + 1}-${last})`, startYear: e2End + 1, endYear: last },
  ];
}

function singleGameRow(m) {
  const row = {
    year: String(m.year), week: m.week,
    owner: m.owner, pts: m.pts,
    opp: m.opp, oppPts: m.opp_pts,
    won: m.won,
  };
  if (m.margin != null) row.margin = m.margin;
  // A1.6 R2 #13 — pass round/place labels (e.g. "Championship", "Semifinal",
  // "3rd Place") through to the renderer when the adapter stamped one.
  if (m.playoffLabel) row.playoffLabel = m.playoffLabel;
  return row;
}

export function computeFunStats(matchups, options = {}) {
  const reg = matchups.filter(m => !m.playoff && !m.consolation);
  const playoffs = matchups.filter(m => m.playoff && !m.consolation);

  const ownersSet = new Set();
  const yearsSet = new Set();
  for (const m of matchups) {
    if (m.owner) ownersSet.add(m.owner);
    if (m.year != null) yearsSet.add(String(m.year));
  }
  const owners = [...ownersSet].sort();
  const years = [...yearsSet].sort();

  const career = {};
  for (const o of owners) career[o] = { owner: o, w: 0, l: 0, pf: 0, pa: 0, games: 0 };
  for (const m of reg) {
    const c = career[m.owner]; if (!c) continue;
    c.games++; c.pf += m.pts; c.pa += m.opp_pts;
    if (m.won) c.w++; else c.l++;
  }
  for (const o of owners) {
    const c = career[o];
    c.winPct = (c.w + c.l) ? c.w / (c.w + c.l) : 0;
    c.ppg = c.games ? c.pf / c.games : 0;
    // Median-match defaults — overwritten below if any season uses the format.
    c.medianW = 0; c.medianL = 0;
    c.totalW = c.w; c.totalL = c.l;
    c.totalWinPct = c.winPct;
  }

  const maxGames = owners.reduce((mx, o) => Math.max(mx, career[o].games), 0);
  const minCoreGames = options.minCoreGames != null
    ? options.minCoreGames
    : Math.max(8, Math.floor(maxGames * 0.5));
  const coreOwners = owners.filter(o => career[o].games >= minCoreGames);

  const eras = detectEras(years, options.eras);

  // SINGLE GAME
  const byScoreDesc = [...reg].sort((a, b) => b.pts - a.pts);
  const byScoreAsc = [...reg].sort((a, b) => a.pts - b.pts);
  const lossesByScore = reg.filter(m => !m.won).sort((a, b) => b.pts - a.pts);
  const winsByScore = reg.filter(m => m.won).sort((a, b) => a.pts - b.pts);
  const winsMargin = reg.filter(m => m.won).map(m => ({ ...m, margin: m.pts - m.opp_pts }));
  const blowouts = [...winsMargin].sort((a, b) => b.margin - a.margin);
  const closest = [...winsMargin].sort((a, b) => a.margin - b.margin);

  const singleGame = {
    highest: byScoreDesc.slice(0, 25).map(singleGameRow),
    lowest: byScoreAsc.slice(0, 15).map(singleGameRow),
    heartbreakers: lossesByScore.slice(0, 15).map(singleGameRow),
    ugliestWins: winsByScore.slice(0, 10).map(singleGameRow),
    biggestBlowouts: blowouts.slice(0, 15).map(singleGameRow),
    closestGames: closest.slice(0, 15).map(singleGameRow),
  };

  // HEAD-TO-HEAD
  const matrix = {};
  for (const o of owners) {
    matrix[o] = {};
    for (const o2 of owners) if (o !== o2) matrix[o][o2] = { w: 0, l: 0 };
  }
  for (const m of reg) {
    const row = matrix[m.owner] && matrix[m.owner][m.opp];
    if (!row) continue;
    if (m.won) row.w++; else row.l++;
  }
  const lopsidedRivalries = [];
  for (let i = 0; i < owners.length; i++) {
    for (let j = i + 1; j < owners.length; j++) {
      const o1 = owners[i], o2 = owners[j];
      const cell = matrix[o1][o2];
      const w1 = cell.w, l1 = cell.l, t = w1 + l1;
      if (t < 10) continue;
      if (w1 > l1 && w1 / t >= 0.6) {
        lopsidedRivalries.push({ winner: o1, loser: o2, w: w1, l: l1, winPct: w1 / t, meetings: t });
      } else if (l1 > w1 && l1 / t >= 0.6) {
        lopsidedRivalries.push({ winner: o2, loser: o1, w: l1, l: w1, winPct: l1 / t, meetings: t });
      }
    }
  }
  lopsidedRivalries.sort((a, b) => b.winPct - a.winPct);
  const headToHead = { matrix, lopsidedRivalries };

  // STREAKS — regular + playoff, exclude consolation, reset on year boundary
  const streakSrc = matchups.filter(m => !m.consolation);
  function bestStreak(owner, predicate) {
    const games = streakSrc.filter(m => m.owner === owner)
      .sort((a, b) => String(a.year).localeCompare(String(b.year)) || a.week - b.week);
    let cur = 0, best = 0, start = null, bestStart = null, prevYear = null;
    for (const g of games) {
      const yr = String(g.year);
      if (yr !== prevYear) { cur = 0; prevYear = yr; }
      if (predicate(g)) {
        if (cur === 0) start = `${yr} Wk${g.week}`;
        cur++;
        if (cur > best) { best = cur; bestStart = start; }
      } else {
        cur = 0;
      }
    }
    return { owner, length: best, startedAt: bestStart };
  }
  const longestWinStreaks = owners
    .map(o => bestStreak(o, g => g.won))
    .sort((a, b) => b.length - a.length);
  const longestLosingStreaks = owners
    .map(o => bestStreak(o, g => !g.won))
    .sort((a, b) => b.length - a.length);

  // SINGLE-SEASON RECORDS
  const ownerSeasonsMap = {};
  for (const m of reg) {
    const k = `${m.owner}|${String(m.year)}`;
    if (!ownerSeasonsMap[k]) ownerSeasonsMap[k] = { owner: m.owner, year: String(m.year), games: [] };
    ownerSeasonsMap[k].games.push(m);
  }
  const seasonRecords = Object.values(ownerSeasonsMap).map(({ owner, year, games }) => {
    const w = games.filter(g => g.won).length;
    const l = games.length - w;
    const pf = games.reduce((s, g) => s + g.pts, 0);
    const pa = games.reduce((s, g) => s + g.opp_pts, 0);
    return {
      owner, year, w, l, pf, pa,
      ppg: games.length ? pf / games.length : 0,
      games: games.length,
      winPct: (w + l) ? w / (w + l) : 0,
    };
  });
  // singleSeason rankings and careerTotals sort are deferred until AFTER
  // the median-compute block populates total* fields. computeChampionships
  // only uses seasonRecords.pf so it's safe to call here.

  // CHAMPIONSHIPS
  const championships = computeChampionships(playoffs, seasonRecords, years, options.championshipsByYear);

  // WEEKLY SCORE INDEX (used by luck + volatility + wrong-week)
  const weeklyScores = {};
  for (const m of reg) {
    const k = `${String(m.year)}|${m.week}`;
    if (!weeklyScores[k]) weeklyScores[k] = {};
    weeklyScores[k][m.owner] = m.pts;
  }

  // MEDIAN / AVERAGE MATCH WINS — leagues that award a second weekly win
  // against the league median (Sleeper `league_average_match`, Yahoo
  // `uses_median_score`). Detection per-row so dynasty leagues that
  // switched formats mid-history compute correctly. Strict `>` against
  // median ⇒ ties at the median line lose. Streaks/playoffs/allPlay
  // explicitly skip medians per spec (a "winning streak" is H2H-only).
  const medianWeekKeys = new Set();
  const medianYears = new Set();
  for (const m of reg) {
    if (m.medianFormat) {
      medianWeekKeys.add(`${String(m.year)}|${m.week}`);
      medianYears.add(String(m.year));
    }
  }
  const formatHasMedianWins = medianWeekKeys.size > 0;
  // For dynasty leagues that adopted medians partway through history (e.g.,
  // Caleb Smith Williams Dynasty switched ON in 2023). Lets the display layer
  // qualify the badge: "MEDIAN WINS (since 2023)" instead of implying
  // every year used the format.
  const medianYearsList = [...medianYears].sort();
  const formatMedianFirstYear = medianYearsList[0] || null;
  // null when every year of available history uses medians (badge can show
  // just "MEDIAN WINS FORMAT").
  const formatMedianAllYears = medianYears.size === years.length;
  const medianBucket = {};
  const seasonMedianBucket = {}; // "owner|year" -> { mw, ml }
  for (const o of owners) medianBucket[o] = { mw: 0, ml: 0 };
  if (formatHasMedianWins) {
    for (const wk of medianWeekKeys) {
      const scores = weeklyScores[wk];
      if (!scores) continue;
      const vals = Object.values(scores).sort((a, b) => a - b);
      const n = vals.length;
      if (n < 2) continue;
      const median = n % 2 === 0
        ? (vals[n / 2 - 1] + vals[n / 2]) / 2
        : vals[(n - 1) / 2];
      const [yearStr] = wk.split('|');
      for (const o in scores) {
        if (!medianBucket[o]) continue;
        const seasonKey = `${o}|${yearStr}`;
        if (!seasonMedianBucket[seasonKey]) seasonMedianBucket[seasonKey] = { mw: 0, ml: 0 };
        if (scores[o] > median) {
          medianBucket[o].mw++;
          seasonMedianBucket[seasonKey].mw++;
        } else {
          medianBucket[o].ml++;
          seasonMedianBucket[seasonKey].ml++;
        }
      }
    }
    for (const o of owners) {
      const c = career[o];
      c.medianW = medianBucket[o].mw;
      c.medianL = medianBucket[o].ml;
      c.totalW = c.w + c.medianW;
      c.totalL = c.l + c.medianL;
      c.totalWinPct = (c.totalW + c.totalL) ? c.totalW / (c.totalW + c.totalL) : 0;
    }
    // Back-inject into seasonRecords (built above with H2H-only fields).
    for (const s of seasonRecords) {
      const b = seasonMedianBucket[`${s.owner}|${s.year}`];
      const mw = b?.mw || 0, ml = b?.ml || 0;
      s.medianW = mw; s.medianL = ml;
      s.totalW = s.w + mw; s.totalL = s.l + ml;
      s.totalWinPct = (s.totalW + s.totalL) ? s.totalW / (s.totalW + s.totalL) : 0;
    }
  } else {
    // Backfill identity values so the schema is uniform downstream.
    for (const s of seasonRecords) {
      s.medianW = 0; s.medianL = 0;
      s.totalW = s.w; s.totalL = s.l;
      s.totalWinPct = s.winPct;
    }
  }

  // singleSeason rankings sort by totalWinPct (== winPct when no medians).
  const singleSeason = {
    bestRecords: [...seasonRecords].sort((a, b) => b.totalWinPct - a.totalWinPct).slice(0, 10),
    worstRecords: [...seasonRecords].sort((a, b) => a.totalWinPct - b.totalWinPct).slice(0, 10),
    highestPPG: [...seasonRecords].sort((a, b) => b.ppg - a.ppg).slice(0, 10),
  };


  // CAREER TOTALS — sort by total wins (== H2H wins when no median format).
  // A1.5 §2.1 — also enriched with playoffApps (distinct years owner made
  // playoffs) and titles (count from championships.titleCounts join). The
  // augmented row drives the full-width Career Totals featured card.
  const playoffYearsByOwnerEarly = {};
  for (const m of playoffs) {
    if (m.consolation) continue;
    if (!playoffYearsByOwnerEarly[m.owner]) playoffYearsByOwnerEarly[m.owner] = new Set();
    playoffYearsByOwnerEarly[m.owner].add(String(m.year));
  }
  const coreOwnerSet = new Set(coreOwners);
  for (const o of owners) {
    const c = career[o];
    c.playoffApps = playoffYearsByOwnerEarly[o]?.size || 0;
    c.titles = championships?.titleCounts?.[o]?.length || 0;
    // A1.6 §2 — `core` flag for the Career Totals card's current/former
    // split. coreOwners = `games >= minCoreGames` (max(8, half the most-
    // active owner's games)). Carrying the boolean per row keeps the
    // component free of duplicate filter logic.
    c.core = coreOwnerSet.has(o);
  }
  const careerTotals = owners.map(o => career[o]).sort((a, b) => b.totalW - a.totalW);

  // LUCK: all-play
  const apBucket = {};
  for (const o of owners) apBucket[o] = { apW: 0, apL: 0, actW: 0, actL: 0 };
  for (const m of reg) {
    const bucket = apBucket[m.owner]; if (!bucket) continue;
    const scores = weeklyScores[`${String(m.year)}|${m.week}`];
    for (const o2 in scores) {
      if (o2 === m.owner) continue;
      const s = scores[o2];
      if (m.pts > s) bucket.apW++;
      else if (m.pts < s) bucket.apL++;
    }
    if (m.won) bucket.actW++; else bucket.actL++;
  }
  const allPlay = coreOwners.map(o => {
    const a = apBucket[o];
    const apTot = a.apW + a.apL, actTot = a.actW + a.actL;
    const apPct = apTot ? a.apW / apTot : 0;
    const actPct = actTot ? a.actW / actTot : 0;
    return {
      owner: o, apW: a.apW, apL: a.apL, apPct,
      actW: a.actW, actL: a.actL, actPct,
      luck: actPct - apPct,
    };
  }).sort((a, b) => b.apPct - a.apPct);

  // LUCK: lucky / unlucky seasons
  const seasonLuck = [];
  for (const s of seasonRecords) {
    if (s.games < 8) continue;
    let apW = 0, apL = 0;
    for (const g of ownerSeasonsMap[`${s.owner}|${s.year}`].games) {
      const scores = weeklyScores[`${String(g.year)}|${g.week}`];
      for (const o2 in scores) {
        if (o2 === s.owner) continue;
        const sc = scores[o2];
        if (g.pts > sc) apW++;
        else if (g.pts < sc) apL++;
      }
    }
    const apPct = (apW + apL) ? apW / (apW + apL) : 0;
    seasonLuck.push({
      owner: s.owner, year: s.year, w: s.w, l: s.l,
      apW, apL, apPct, actPct: s.winPct,
      luck: s.winPct - apPct,
    });
  }
  const luckiestSeasons = [...seasonLuck].sort((a, b) => b.luck - a.luck).slice(0, 10);
  const unluckiestSeasons = [...seasonLuck].sort((a, b) => a.luck - b.luck).slice(0, 10);

  // LUCK: points-against (opponent score vs their season avg)
  const ownerSeasonAvg = {};
  for (const s of seasonRecords) ownerSeasonAvg[`${s.owner}|${s.year}`] = s.ppg;
  const paBucket = {};
  for (const o of owners) paBucket[o] = { above: 0, below: 0, totalDiff: 0 };
  for (const m of reg) {
    const oppAvg = ownerSeasonAvg[`${m.opp}|${String(m.year)}`];
    if (oppAvg == null || oppAvg <= 0) continue;
    const bucket = paBucket[m.owner]; if (!bucket) continue;
    const diff = m.opp_pts - oppAvg;
    bucket.totalDiff += diff;
    if (diff > 0) bucket.above++; else bucket.below++;
  }
  const pointsAgainst = coreOwners.map(o => {
    const p = paBucket[o];
    const tot = p.above + p.below;
    return {
      owner: o, above: p.above, below: p.below,
      abovePct: tot ? p.above / tot : 0,
      avgDiff: tot ? p.totalDiff / tot : 0,
    };
  }).sort((a, b) => b.abovePct - a.abovePct);

  const luck = { allPlay, luckiestSeasons, unluckiestSeasons, pointsAgainst };

  // VOLATILITY: weekly top/bottom + boom-bust
  const weeklyChamp = {}, basementWeeks = {};
  for (const o of owners) { weeklyChamp[o] = 0; basementWeeks[o] = 0; }
  for (const k in weeklyScores) {
    const scores = weeklyScores[k];
    const vals = Object.values(scores);
    if (vals.length < 3) continue;
    const best = Math.max(...vals), worst = Math.min(...vals);
    for (const o in scores) {
      if (scores[o] === best) weeklyChamp[o]++;
      if (scores[o] === worst) basementWeeks[o]++;
    }
  }
  const weeklyTopBottom = coreOwners.map(o => {
    const g = career[o].games;
    return {
      owner: o, top: weeklyChamp[o], bottom: basementWeeks[o], games: g,
      topPct: g ? weeklyChamp[o] / g : 0,
      bottomPct: g ? basementWeeks[o] / g : 0,
    };
  }).sort((a, b) => b.top - a.top);

  const ownerWeekly = {};
  for (const o of owners) ownerWeekly[o] = [];
  for (const m of reg) if (ownerWeekly[m.owner]) ownerWeekly[m.owner].push(m.pts);
  const boomBust = coreOwners.map(o => {
    const pts = ownerWeekly[o];
    if (pts.length < 2) return null;
    const avg = mean(pts), sd = stdev(pts);
    return {
      owner: o, avgPts: avg, stdDev: sd,
      coefVar: avg ? sd / avg : 0,
      min: Math.min(...pts), max: Math.max(...pts),
    };
  }).filter(Boolean).sort((a, b) => b.stdDev - a.stdDev);

  const volatility = { weeklyTopBottom, boomBust };

  // YOY SWINGS
  const yoyList = [];
  for (const r of seasonRecords) {
    if (r.games < 8) continue;
    const prev = seasonRecords.find(s =>
      s.owner === r.owner && s.games >= 8 && Number(s.year) === Number(r.year) - 1
    );
    if (!prev) continue;
    yoyList.push({
      owner: r.owner,
      // Display fields use totals when the league uses median wins; identical
      // to H2H w/l when it doesn't (totalW/L are backfilled in non-median branch).
      year: r.year, w: r.totalW, l: r.totalL,
      prevYear: prev.year, prevW: prev.totalW, prevL: prev.totalL,
      swing: r.totalWinPct - prev.totalWinPct,
      winSwing: r.totalW - prev.totalW,
    });
  }
  const yoySwings = {
    biggestComebacks: [...yoyList].sort((a, b) => b.swing - a.swing).slice(0, 10),
    biggestFalls: [...yoyList].sort((a, b) => a.swing - b.swing).slice(0, 10),
  };

  // ERA RANKINGS — accumulate H2H and median totals per (owner, era).
  // When the league doesn't use medians, medianW/L stay 0 ⇒ totalW = w.
  const eraRankings = {};
  for (const era of eras) {
    const eraReg = reg.filter(m => {
      const y = Number(m.year);
      return y >= era.startYear && y <= era.endYear;
    });
    if (!eraReg.length) continue;
    const ec = {};
    for (const o of owners) ec[o] = { w: 0, l: 0, mw: 0, ml: 0, pf: 0, games: 0 };
    for (const m of eraReg) {
      const c = ec[m.owner]; if (!c) continue;
      c.games++; c.pf += m.pts;
      if (m.won) c.w++; else c.l++;
    }
    if (formatHasMedianWins) {
      for (const wk of medianWeekKeys) {
        const [yearStr] = wk.split('|');
        const yr = Number(yearStr);
        if (yr < era.startYear || yr > era.endYear) continue;
        const scores = weeklyScores[wk];
        if (!scores) continue;
        const vals = Object.values(scores).sort((a, b) => a - b);
        const n = vals.length;
        if (n < 2) continue;
        const median = n % 2 === 0
          ? (vals[n / 2 - 1] + vals[n / 2]) / 2
          : vals[(n - 1) / 2];
        for (const o in scores) {
          if (!ec[o]) continue;
          if (scores[o] > median) ec[o].mw++; else ec[o].ml++;
        }
      }
    }
    eraRankings[era.name] = owners
      .filter(o => ec[o].games >= 10)
      .map(o => {
        const c = ec[o];
        const totalW = c.w + c.mw, totalL = c.l + c.ml;
        const totalTot = totalW + totalL;
        const h2hTot = c.w + c.l;
        return {
          owner: o,
          w: totalW, l: totalL,
          h2hW: c.w, h2hL: c.l, medianW: c.mw, medianL: c.ml,
          winPct: totalTot ? totalW / totalTot : 0,
          h2hWinPct: h2hTot ? c.w / h2hTot : 0,
          ppg: c.games ? c.pf / c.games : 0,
          games: c.games,
        };
      })
      .sort((a, b) => b.winPct - a.winPct);
  }

  // PLAYOFFS
  const pCareer = {};
  for (const o of owners) pCareer[o] = { w: 0, l: 0, pf: 0, games: 0 };
  for (const m of playoffs) {
    const c = pCareer[m.owner]; if (!c) continue;
    c.games++; c.pf += m.pts;
    if (m.won) c.w++; else c.l++;
  }
  const playoffCareerRecords = owners
    .filter(o => pCareer[o].games > 0)
    .map(o => {
      const c = pCareer[o];
      const tot = c.w + c.l;
      return {
        owner: o, w: c.w, l: c.l,
        winPct: tot ? c.w / tot : 0,
        ppg: c.games ? c.pf / c.games : 0,
        games: c.games,
      };
    })
    .sort((a, b) => b.winPct - a.winPct);

  const playoffSeasonsMap = {};
  for (const m of playoffs) {
    const k = `${m.owner}|${String(m.year)}`;
    if (!playoffSeasonsMap[k]) playoffSeasonsMap[k] = { owner: m.owner, year: String(m.year), games: [] };
    playoffSeasonsMap[k].games.push(m);
  }
  const playoffHighestPPG = Object.values(playoffSeasonsMap)
    .filter(s => s.games.length >= 2)
    .map(s => {
      const ppg = mean(s.games.map(g => g.pts));
      const w = s.games.filter(g => g.won).length;
      return { owner: s.owner, year: s.year, ppg, games: s.games.length, w, l: s.games.length - w };
    })
    .sort((a, b) => b.ppg - a.ppg)
    .slice(0, 10);

  // A1.6 §5 — closest playoff matchups. The existing singleGame.closestGames
  // is sourced from winsMargin, which is regular-season-only. We walk the
  // playoffs array separately so the Playoffs section can surface its own
  // closest-margin subtable. Wins-only to dedupe each matchup to one row.
  const playoffClosest = playoffs
    .filter(m => m.won)
    .map(m => ({ ...m, margin: m.pts - m.opp_pts }))
    .sort((a, b) => a.margin - b.margin)
    .slice(0, 10)
    .map(singleGameRow);
  const playoffsOut = {
    careerRecords: playoffCareerRecords,
    highestPPG: playoffHighestPPG,
    closestGames: playoffClosest,
  };

  // AWARDS: late-season clutch
  const seasonLastWeek = {};
  for (const m of reg) {
    const y = String(m.year);
    seasonLastWeek[y] = Math.max(seasonLastWeek[y] || 0, m.week);
  }
  const lateBucket = {};
  for (const o of owners) lateBucket[o] = { w: 0, l: 0 };
  for (const m of reg) {
    const last = seasonLastWeek[String(m.year)];
    if (m.week >= last - 2) {
      const b = lateBucket[m.owner]; if (!b) continue;
      if (m.won) b.w++; else b.l++;
    }
  }
  const lateSeasonClutch = coreOwners
    .filter(o => lateBucket[o].w + lateBucket[o].l > 0)
    .map(o => {
      const b = lateBucket[o];
      const tot = b.w + b.l;
      return { owner: o, w: b.w, l: b.l, winPct: tot ? b.w / tot : 0 };
    })
    .sort((a, b) => b.winPct - a.winPct);

  // AWARDS: wrong week (top-3 league score but lost)
  const wwBucket = {};
  for (const o of owners) wwBucket[o] = { count: 0, games: [] };
  for (const m of reg) {
    if (m.won) continue;
    const scores = weeklyScores[`${String(m.year)}|${m.week}`];
    const sorted = Object.values(scores).sort((a, b) => b - a);
    if (sorted.length >= 3 && m.pts >= sorted[2]) {
      const b = wwBucket[m.owner]; if (!b) continue;
      b.count++; b.games.push(m);
    }
  }
  const wrongWeek = coreOwners
    .filter(o => wwBucket[o].count > 0)
    .map(o => {
      const b = wwBucket[o];
      const games = career[o].games;
      return {
        owner: o, count: b.count, games,
        rate: games ? b.count / games : 0,
        mostPainful: [...b.games].sort((a, b) => b.pts - a.pts).slice(0, 3).map(singleGameRow),
      };
    })
    .sort((a, b) => b.count - a.count);

  // AWARDS: punching bag (losses by 30+)
  const punchingBag = coreOwners
    .map(o => {
      const blow = reg.filter(m => m.owner === o && !m.won && (m.opp_pts - m.pts) >= 30).length;
      const games = career[o].games;
      return { owner: o, blowoutLosses: blow, games, rate: games ? blow / games : 0 };
    })
    .filter(r => r.blowoutLosses > 0)
    .sort((a, b) => b.blowoutLosses - a.blowoutLosses);

  // AWARDS: one-hit wonder vs dynasty
  const oneHitWonder = [];
  for (const o of coreOwners) {
    const seasons = seasonRecords.filter(r => r.owner === o && r.games >= 8);
    if (seasons.length < 3) continue;
    // Use totalWinPct so the volatility metric stays consistent with the
    // record displayed below (best.totalW-best.totalL).
    const wpcts = seasons.map(s => s.totalWinPct);
    const best = seasons.reduce((a, b) => a.totalWinPct > b.totalWinPct ? a : b);
    const worst = seasons.reduce((a, b) => a.totalWinPct < b.totalWinPct ? a : b);
    oneHitWonder.push({
      owner: o,
      avgWinPct: mean(wpcts),
      stdDev: stdev(wpcts),
      seasons: seasons.length,
      // Record strings reflect totals when format uses medians (totalW = w
      // when it doesn't, so this is correct in both cases).
      bestYear: best.year, bestRecord: `${best.totalW}-${best.totalL}`,
      worstYear: worst.year, worstRecord: `${worst.totalW}-${worst.totalL}`,
    });
  }
  oneHitWonder.sort((a, b) => b.stdDev - a.stdDev);

  const awards = { lateSeasonClutch, wrongWeek, punchingBag, oneHitWonder };

  return {
    meta: {
      yearsCovered: years,
      totalMatchups: reg.length,
      totalPlayoffMatchups: playoffs.length,
      totalOwners: owners.length,
      coreOwners,
      minCoreGames,
      erasDetected: eras,
      // Median/average-match flag. When true, record fields throughout the
      // payload (careerTotals, seasonRecords, eraRankings, yoySwings,
      // oneHitWonder) reflect totals (H2H + median). When false, totals
      // equal H2H — schema is uniform. Streaks/playoffs/allPlay are
      // intentionally H2H-only (see compute block above).
      formatHasMedianWins,
      // For dynasty leagues that switched mid-history: first season with
      // medians enabled. null when no medians at all. formatMedianAllYears
      // tells the display whether to qualify the badge with "since YYYY".
      formatMedianFirstYear,
      formatMedianAllYears,
      // Tier 2 — Sleeper's stats endpoint floor. Drafts in pre-floor years
      // get statsCoverage:"draft_only" and skip bust/steal/MVP analysis.
      // Trivia generator + validator both gate player-centric claims on
      // this year to avoid hallucinations about pre-coverage seasons.
      earliestStatsYear: options.earliestStatsYear ?? null,
      // A1 awards refactor — dynasty leagues unlock 2 extra YoY awards
      // (Biggest Collapse, Comeback Kid) in the Awards section. Detection
      // via draftEventType: any 'startup' or 'rookie' draft ⇒ dynasty.
      // Redraft leagues have all drafts tagged 'annual'.
      isDynasty: Array.isArray(options.drafts) && options.drafts.some(
        (d) => d?.draftEventType === 'startup' || d?.draftEventType === 'rookie'
      ),
    },
    singleGame,
    headToHead,
    streaks: { longestWinStreaks, longestLosingStreaks },
    singleSeason,
    careerTotals,
    championships,
    luck,
    volatility,
    yoySwings,
    eraRankings,
    playoffs: playoffsOut,
    awards,
    // Tier 1 draft data — adapter output, optionally enriched per-pick
    // with seasonStats / valueOverExpected / isBust / isSteal (Tier 2).
    // Drafts whose season is below SLEEPER_EARLIEST_STATS_YEAR carry
    // statsCoverage:"draft_only" and skip all bust/steal/MVP work.
    drafts: computeDraftAnalysis(options.drafts),
    // Tier 2 cross-draft aggregations. Empty when no drafts have Tier 2
    // coverage (e.g., pure Yahoo or fully pre-2009 history).
    draftMeta: computeDraftMeta(options.drafts),
    // Trades (task #38) — adapter output passes through unchanged; the
    // meta aggregations below feed the section highlight + future
    // analysis bullets.
    trades: Array.isArray(options.trades) ? options.trades : [],
    tradeMeta: computeTradeMeta(options.trades),
    // UX_REFINEMENTS_v2 §2 — Spotify-Wrapped-style TL;DR summary that
    // renders between share cards and hero bullets. All values derived
    // from data already computed above so this is cheap.
    leagueOverview: computeLeagueOverview({
      years, coreOwners, championships, singleSeason, singleGame,
      drafts: options.drafts,
      trades: options.trades,
    }),
    // A1 awards refactor — pre-resolved 12-award map keyed by slug. Each
    // value is { slug, label, icon, owner, value, context } or null when
    // the underlying data isn't sufficient. Renderer maps slug → display.
    // Dynasty-only awards (biggestCollapse, comebackKid) are null on
    // redraft leagues.
    almanacAwards: computeAlmanacAwards({
      reg, playoffs, championships, seasonRecords,
      streaks: { longestWinStreaks, longestLosingStreaks },
      headToHead: { matrix },
      yoySwings,
      tradeMeta: computeTradeMeta(options.trades),
      years,
      isDynasty: Array.isArray(options.drafts) && options.drafts.some(
        (d) => d?.draftEventType === 'startup' || d?.draftEventType === 'rookie'
      ),
    }),
  };
}

// Trade aggregations (task #38) — feeds the section highlight + future
// "most active trader" / "lopsided trade pair" bullets. Pure derivation
// over adapter output, no new traversal of the raw transaction firehose.
function computeTradeMeta(trades) {
  if (!Array.isArray(trades) || !trades.length) {
    return { totalTrades: 0, seasonsWithTrades: 0, topPair: null, perOwner: {} };
  }
  const seasonsSet = new Set();
  // Pair count uses a normalized "ownerA||ownerB" key (sorted alpha so
  // the pair is order-independent). Multi-team trades (3+ participants)
  // contribute to every pairwise combination they generate.
  const pairCounts = {};
  const perOwner = {};
  for (const t of trades) {
    seasonsSet.add(t.season);
    const owners = (t.participants || []).map(p => p.owner).filter(Boolean);
    for (const o of owners) perOwner[o] = (perOwner[o] || 0) + 1;
    for (let i = 0; i < owners.length; i++) {
      for (let j = i + 1; j < owners.length; j++) {
        const [a, b] = [owners[i], owners[j]].sort();
        const k = `${a}||${b}`;
        pairCounts[k] = (pairCounts[k] || 0) + 1;
      }
    }
  }
  let topPair = null;
  for (const [k, count] of Object.entries(pairCounts)) {
    if (!topPair || count > topPair.count) {
      const [a, b] = k.split('||');
      topPair = { ownerA: a, ownerB: b, count };
    }
  }
  return {
    totalTrades: trades.length,
    seasonsWithTrades: seasonsSet.size,
    topPair,
    perOwner,
  };
}

// A1 awards refactor — 10 universal + 2 dynasty-only awards, pre-resolved
// into { slug, label, icon, owner, value, context } shape. Returned as a
// keyed object so renderers can pick by slug (spotlight wants 4 specific
// ones; section wants all 10 or 12). Compute is O(matchups) total —
// every helper takes ≤ one matchup pass.
//
// Two notes on derivation honesty:
//   - Hot Hand / Cold Snap reuse stats.streaks.longestWinStreaks[0] which
//     is already single-season (bestStreak() resets cur=0 on year boundary).
//   - Playoff Mainstay uses the m.playoff flag directly. Verified
//     consistent across Sleeper (derived from playoff_week_start) and
//     Yahoo (is_playoffs from API). Absence breaks the streak — simplest
//     definition, matches the spec's "consecutive playoff appearances"
//     literal reading.
function computeAlmanacAwards({ reg, playoffs, championships, seasonRecords, streaks, headToHead, yoySwings, tradeMeta, years, isDynasty }) {
  const currentYear = years.length ? Number(years[years.length - 1]) : new Date().getFullYear();

  // --- 1. Bridesmaid — most runner-up finishes, gated on zero titles ---
  // A1.5 §2.4 — track which years contributed for click-to-expand
  const runnerUpYearsByOwner = {};
  for (const y of championships?.years || []) {
    if (!y.runnerUp) continue;
    if (!runnerUpYearsByOwner[y.runnerUp]) runnerUpYearsByOwner[y.runnerUp] = [];
    runnerUpYearsByOwner[y.runnerUp].push({ year: y.year, champion: y.champion });
  }
  const neverWon = (owner) => !(championships?.titleCounts?.[owner]?.length > 0);
  let bridesmaidPick = null;
  for (const [owner, runnerUps] of Object.entries(runnerUpYearsByOwner)) {
    if (!neverWon(owner)) continue;
    if (!bridesmaidPick || runnerUps.length > bridesmaidPick.count) {
      bridesmaidPick = { owner, count: runnerUps.length, runnerUps };
    }
  }
  const bridesmaid = bridesmaidPick && {
    slug: "bridesmaid", label: "Bridesmaid", icon: "🏆",
    owner: bridesmaidPick.owner, value: bridesmaidPick.count,
    context: `${bridesmaidPick.count} runner-up${bridesmaidPick.count === 1 ? "" : "s"}, 0 titles`,
    details: bridesmaidPick.runnerUps, // [{year, champion}, ...]
  };

  // --- 2. Top Dog — most top-3 single-season PPG finishes ---
  const seasonsByYear = {};
  for (const s of seasonRecords || []) {
    if (!seasonsByYear[s.year]) seasonsByYear[s.year] = [];
    seasonsByYear[s.year].push(s);
  }
  const top3ByOwner = {};
  for (const yearSeasons of Object.values(seasonsByYear)) {
    const sorted = [...yearSeasons].sort((a, b) => b.ppg - a.ppg);
    for (let i = 0; i < Math.min(3, sorted.length); i++) {
      const o = sorted[i].owner;
      if (!top3ByOwner[o]) top3ByOwner[o] = [];
      top3ByOwner[o].push({ year: sorted[i].year, ppg: Number(sorted[i].ppg.toFixed(1)), rank: i + 1 });
    }
  }
  let topDogPick = null;
  for (const [owner, finishes] of Object.entries(top3ByOwner)) {
    if (!topDogPick || finishes.length > topDogPick.count) {
      topDogPick = { owner, count: finishes.length, finishes };
    }
  }
  const topDog = topDogPick && {
    slug: "topDog", label: "Top Dog", icon: "🔥",
    owner: topDogPick.owner, value: topDogPick.count,
    context: `${topDogPick.count} top-3 PPG finish${topDogPick.count === 1 ? "" : "es"}`,
    details: topDogPick.finishes.sort((a, b) => Number(b.year) - Number(a.year)),
  };

  // --- 3. Heartbreaker — most losses by margin ≤ 3 pts ---
  // Track per-owner game list for click-to-expand.
  const heartbreakByOwner = {};
  for (const m of reg) {
    if (!m.won) {
      const margin = (m.opp_pts || 0) - (m.pts || 0);
      if (margin > 0 && margin <= 3) {
        if (!heartbreakByOwner[m.owner]) heartbreakByOwner[m.owner] = [];
        heartbreakByOwner[m.owner].push({
          year: m.year, week: m.week, opp: m.opp,
          pts: Number((m.pts || 0).toFixed(1)),
          oppPts: Number((m.opp_pts || 0).toFixed(1)),
          margin: Number(margin.toFixed(1)),
        });
      }
    }
  }
  let heartbreakerPick = null;
  for (const [owner, games] of Object.entries(heartbreakByOwner)) {
    if (!heartbreakerPick || games.length > heartbreakerPick.count) {
      heartbreakerPick = { owner, count: games.length, games };
    }
  }
  const heartbreaker = heartbreakerPick && {
    slug: "heartbreaker", label: "Heartbreaker", icon: "🥀",
    owner: heartbreakerPick.owner, value: heartbreakerPick.count,
    context: `${heartbreakerPick.count} loss${heartbreakerPick.count === 1 ? "" : "es"} by ≤3 pts`,
    details: heartbreakerPick.games.sort((a, b) => Number(b.year) - Number(a.year) || b.week - a.week),
  };

  // --- 4. Escape Artist — most wins by margin ≤ 3 pts ---
  const escapeByOwner = {};
  for (const m of reg) {
    if (m.won) {
      const margin = (m.pts || 0) - (m.opp_pts || 0);
      if (margin > 0 && margin <= 3) {
        if (!escapeByOwner[m.owner]) escapeByOwner[m.owner] = [];
        escapeByOwner[m.owner].push({
          year: m.year, week: m.week, opp: m.opp,
          pts: Number((m.pts || 0).toFixed(1)),
          oppPts: Number((m.opp_pts || 0).toFixed(1)),
          margin: Number(margin.toFixed(1)),
        });
      }
    }
  }
  let escapePick = null;
  for (const [owner, games] of Object.entries(escapeByOwner)) {
    if (!escapePick || games.length > escapePick.count) {
      escapePick = { owner, count: games.length, games };
    }
  }
  const escapeArtist = escapePick && {
    slug: "escapeArtist", label: "Escape Artist", icon: "🍀",
    owner: escapePick.owner, value: escapePick.count,
    context: `${escapePick.count} win${escapePick.count === 1 ? "" : "s"} by ≤3 pts`,
    details: escapePick.games.sort((a, b) => Number(b.year) - Number(a.year) || b.week - a.week),
  };

  // --- 5. Hot Hand — reuse single-season longest win streak ---
  // Walk the owner's matchups in the streak window to capture per-week
  // detail (opponent + scores) for click-to-expand.
  const hh = streaks?.longestWinStreaks?.find((s) => (s?.length || 0) > 0);
  const hotHand = hh && {
    slug: "hotHand", label: "Hot Hand", icon: "🔥",
    owner: hh.owner, value: hh.length,
    context: hh.startedAt ? `${hh.length}-game streak from ${hh.startedAt}` : `${hh.length}-game streak`,
    details: deriveStreakWindow(reg.concat(playoffs.filter(p => !p.consolation)), hh.owner, hh.startedAt, hh.length, true),
  };

  // --- 6. Cold Snap — reuse single-season longest losing streak ---
  const cs = streaks?.longestLosingStreaks?.find((s) => (s?.length || 0) > 0);
  const coldSnap = cs && {
    slug: "coldSnap", label: "Cold Snap", icon: "❄️",
    owner: cs.owner, value: cs.length,
    context: cs.startedAt ? `${cs.length}-game skid from ${cs.startedAt}` : `${cs.length}-game skid`,
    details: deriveStreakWindow(reg.concat(playoffs.filter(p => !p.consolation)), cs.owner, cs.startedAt, cs.length, false),
  };

  // --- 7. Playoff Mainstay — longest consecutive playoff appearances.
  // Absence breaks the streak (simple definition per A1 audit). Filter
  // out consolation games so a "9th place bracket" doesn't count as
  // making it. m.playoff is reliable across Sleeper + Yahoo.
  const playoffYearsByOwner = {};
  for (const m of playoffs) {
    if (m.consolation) continue;
    if (!playoffYearsByOwner[m.owner]) playoffYearsByOwner[m.owner] = new Set();
    playoffYearsByOwner[m.owner].add(Number(m.year));
  }
  let mainstayPick = null;
  for (const [owner, yearSet] of Object.entries(playoffYearsByOwner)) {
    const sortedYrs = [...yearSet].sort((a, b) => a - b);
    let cur = 1, best = 1, bestStart = sortedYrs[0], curStart = sortedYrs[0];
    for (let i = 1; i < sortedYrs.length; i++) {
      if (sortedYrs[i] === sortedYrs[i - 1] + 1) {
        cur++;
        if (cur > best) { best = cur; bestStart = curStart; }
      } else {
        cur = 1;
        curStart = sortedYrs[i];
      }
    }
    if (best >= 2 && (!mainstayPick || best > mainstayPick.length)) {
      mainstayPick = { owner, length: best, startYear: bestStart };
    }
  }
  const playoffMainstay = mainstayPick && {
    slug: "playoffMainstay", label: "Playoff Mainstay", icon: "💎",
    owner: mainstayPick.owner, value: mainstayPick.length,
    context: `${mainstayPick.length} years straight from ${mainstayPick.startYear}`,
    details: Array.from({ length: mainstayPick.length }, (_, i) => ({
      year: String(mainstayPick.startYear + i),
    })),
  };

  // --- 8. Owns Yo' Soul — recompute lopsided rivalries with N ≥ 5.
  // The existing stats.headToHead.lopsidedRivalries gates at N ≥ 10 for
  // the section table; the award uses a separate N ≥ 5 walk so leagues
  // with fewer cross-meetings still surface a winner.
  let soulPick = null;
  const matrix = headToHead?.matrix || {};
  for (const o1 of Object.keys(matrix)) {
    for (const o2 of Object.keys(matrix[o1] || {})) {
      const cell = matrix[o1][o2];
      const total = (cell.w || 0) + (cell.l || 0);
      if (total < 5) continue;
      const wp = cell.w / total;
      if (wp >= 0.6 && (!soulPick || wp > soulPick.winPct)) {
        soulPick = { winner: o1, loser: o2, w: cell.w, l: cell.l, winPct: wp, meetings: total };
      }
    }
  }
  // Per-meeting detail for the picked owner-pair. Walk all non-consolation
  // matchups to find direct head-to-head games between winner and loser.
  let soulGames = [];
  if (soulPick) {
    for (const m of reg.concat(playoffs.filter(p => !p.consolation))) {
      if (m.owner === soulPick.winner && m.opp === soulPick.loser) {
        soulGames.push({
          year: m.year, week: m.week,
          ownerPts: Number((m.pts || 0).toFixed(1)),
          opp: m.opp,
          oppPts: Number((m.opp_pts || 0).toFixed(1)),
          won: !!m.won,
          playoff: !!m.playoff,
        });
      }
    }
    soulGames.sort((a, b) => Number(b.year) - Number(a.year) || b.week - a.week);
  }
  const ownsYoSoul = soulPick && {
    slug: "ownsYoSoul", label: "Owns Yo' Soul", icon: "😈",
    owner: soulPick.winner, value: `${soulPick.w}-${soulPick.l}`,
    context: `vs ${soulPick.loser} · ${Math.round(soulPick.winPct * 100)}% (${soulPick.meetings} meetings)`,
    details: { opponent: soulPick.loser, games: soulGames },
  };

  // --- 9. Wheeler & Dealer — most trades made ---
  // details left null here; renderer filters stats.trades at render time
  // (trades have full per-participant asset shape — easier than serializing
  // a snapshot into the award).
  //
  // Graceful empty per A1.5 spec: leagues with no trade history at all
  // (Smithtown pre-2022 was auction-redraft with no traded picks; some
  // Yahoo years have limited trade visibility) still render the award
  // row with a "No trades recorded" placeholder rather than dropping it.
  const tradeCounts = tradeMeta?.perOwner || {};
  const wheelerPick = pickMaxEntry(tradeCounts);
  const wheelerDealer = wheelerPick
    ? {
        slug: "wheelerDealer", label: "Wheeler & Dealer", icon: "🤝",
        owner: wheelerPick[0], value: wheelerPick[1],
        context: `${wheelerPick[1]} trade${wheelerPick[1] === 1 ? "" : "s"} made`,
      }
    : {
        slug: "wheelerDealer", label: "Wheeler & Dealer", icon: "🤝",
        owner: null, value: 0,
        context: "No trades recorded in league history",
        empty: true,
      };

  // --- 10. Title Drought — longest stretch without a title. Two
  // categories compete: never-won owners (drought = seasons played) and
  // past winners (drought = currentYear - lastTitleYear). On ties, prefer
  // never-won (narrative weight per A1 audit decision).
  const ownerSeasonYears = {};
  for (const s of seasonRecords || []) {
    if (!ownerSeasonYears[s.owner]) ownerSeasonYears[s.owner] = new Set();
    ownerSeasonYears[s.owner].add(Number(s.year));
  }
  let droughtPick = null;
  for (const owner of Object.keys(ownerSeasonYears)) {
    const titleYears = (championships?.titleCounts?.[owner] || []).map(Number).sort();
    const hasTitle = titleYears.length > 0;
    let droughtYears, ctx;
    if (!hasTitle) {
      droughtYears = ownerSeasonYears[owner].size;
      ctx = `${droughtYears} season${droughtYears === 1 ? "" : "s"}, no title`;
    } else {
      const lastTitle = Math.max(...titleYears);
      droughtYears = currentYear - lastTitle;
      ctx = `Last title ${lastTitle}`;
    }
    if (droughtYears <= 0) continue;
    if (!droughtPick) {
      droughtPick = { owner, years: droughtYears, hasTitle, context: ctx };
      continue;
    }
    // Strict max wins; ties prefer never-won owners.
    if (droughtYears > droughtPick.years) {
      droughtPick = { owner, years: droughtYears, hasTitle, context: ctx };
    } else if (droughtYears === droughtPick.years && !hasTitle && droughtPick.hasTitle) {
      droughtPick = { owner, years: droughtYears, hasTitle, context: ctx };
    }
  }
  // Years-list detail. For never-won: every season they participated in.
  // For past-winner: every season after their last title.
  let droughtDetails = null;
  if (droughtPick) {
    const titleYears = (championships?.titleCounts?.[droughtPick.owner] || []).map(Number).sort();
    const lastTitle = titleYears.length ? Math.max(...titleYears) : null;
    const yearsSet = ownerSeasonYears[droughtPick.owner] || new Set();
    droughtDetails = [...yearsSet]
      .filter((y) => lastTitle === null || y > lastTitle)
      .sort((a, b) => b - a)
      .map((y) => ({ year: String(y) }));
  }
  // A1.6 R2 #11 — gate Title Drought to leagues where there's a clear
  // outlier. In young/dynasty leagues most owners are title-less, so
  // "longest drought" is whoever sorted first among many ties — meaningless.
  // Hide when ≥50% of owners have never won (no standout) OR when the
  // leader's drought is tied with the runner-up among never-won owners
  // (no daylight = no outlier). DSFTA reference point: 11/14 = 79%
  // title-less → hidden; an established 10-year league with 8 of 10 owners
  // having ever won → 20% title-less → shown.
  const ownersSeen = Object.keys(ownerSeasonYears);
  const titledOwnerCount = ownersSeen.filter(o =>
    ((championships?.titleCounts?.[o] || []).length) > 0
  ).length;
  const titlelessRatio = ownersSeen.length
    ? (ownersSeen.length - titledOwnerCount) / ownersSeen.length
    : 1;
  // Count of owners tied with the drought leader (among never-won, since
  // never-won is preferred on ties). If many tied, no single standout.
  const neverWonAtLeaderYears = droughtPick && !droughtPick.hasTitle
    ? ownersSeen.filter(o => {
        const has = ((championships?.titleCounts?.[o] || []).length) > 0;
        if (has) return false;
        const years = ownerSeasonYears[o].size;
        return years === droughtPick.years;
      }).length
    : 1;
  const droughtIsOutlier = titlelessRatio < 0.5 && neverWonAtLeaderYears <= 2;
  const titleDrought = (droughtPick && droughtIsOutlier) ? {
    slug: "titleDrought", label: "Title Drought", icon: "🎤",
    owner: droughtPick.owner, value: droughtPick.years,
    context: droughtPick.context,
    details: droughtDetails,
  } : null;

  // --- 11/12. Dynasty-only YoY awards. Null on redraft leagues so the
  // renderer can skip them without filtering by isDynasty flag separately.
  let biggestCollapse = null;
  let comebackKid = null;
  if (isDynasty) {
    const fall = yoySwings?.biggestFalls?.[0];
    if (fall) {
      biggestCollapse = {
        slug: "biggestCollapse", label: "Biggest Collapse", icon: "📉",
        owner: fall.owner, value: `${(fall.swing * 100).toFixed(1)}%`,
        context: `${fall.prevYear}→${fall.year}`,
        details: {
          prevYear: fall.prevYear, prevW: fall.prevW, prevL: fall.prevL,
          year: fall.year, w: fall.w, l: fall.l,
          swing: Number((fall.swing * 100).toFixed(1)),
        },
      };
    }
    const rise = yoySwings?.biggestComebacks?.[0];
    if (rise) {
      comebackKid = {
        slug: "comebackKid", label: "Comeback Kid", icon: "🚀",
        owner: rise.owner, value: `+${(rise.swing * 100).toFixed(1)}%`,
        context: `${rise.prevYear}→${rise.year}`,
        details: {
          prevYear: rise.prevYear, prevW: rise.prevW, prevL: rise.prevL,
          year: rise.year, w: rise.w, l: rise.l,
          swing: Number((rise.swing * 100).toFixed(1)),
        },
      };
    }
  }

  return {
    bridesmaid,
    topDog,
    heartbreaker,
    escapeArtist,
    hotHand,
    coldSnap,
    playoffMainstay,
    ownsYoSoul,
    wheelerDealer,
    titleDrought,
    biggestCollapse,
    comebackKid,
  };
}

// Helper — returns the [key, value] pair with the largest value, or null
// when the map is empty. Used by ~6 of the award computes above.
function pickMaxEntry(map) {
  let best = null;
  for (const [key, value] of Object.entries(map)) {
    if (!best || value > best[1]) best = [key, value];
  }
  return best;
}

// A1.5 §2.4 — derive the week-by-week games during a Hot Hand / Cold Snap
// streak. `bestStreak` only returns {owner, length, startedAt} where
// startedAt is "YYYY WkN". Walk the owner's matchups in chronological
// order within that year starting at the given week, collect `length`
// consecutive results matching the expectedWon flag. The result feeds
// the click-to-expand detail view in the Awards section.
function deriveStreakWindow(matchups, owner, startedAt, length, expectedWon) {
  if (!startedAt || !length) return [];
  const m = String(startedAt).match(/^(\d{4})\s*Wk\s*(\d+)/i);
  if (!m) return [];
  const startYear = m[1];
  const startWeek = Number(m[2]);
  // Owner's games in the streak year, sorted chronologically.
  const ownerGames = matchups
    .filter((g) => g.owner === owner && String(g.year) === startYear && g.week >= startWeek)
    .sort((a, b) => a.week - b.week);
  const out = [];
  for (const g of ownerGames) {
    if (!!g.won !== expectedWon) break;
    out.push({
      year: g.year, week: g.week, opp: g.opp,
      pts: Number((g.pts || 0).toFixed(1)),
      oppPts: Number((g.opp_pts || 0).toFixed(1)),
      won: !!g.won,
      playoff: !!g.playoff,
    });
    if (out.length >= length) break;
  }
  return out;
}

// UX_REFINEMENTS_v2 §2 — top-of-page summary card. Pure-derivation function
// over already-computed stats; no new traversals of the matchup array.
function computeLeagueOverview({ years, coreOwners, championships, singleSeason, singleGame, drafts, trades }) {
  const totalSeasons = years.length;
  const totalOwners = coreOwners.length;
  const totalChampionships = Array.isArray(championships?.years)
    ? championships.years.filter(y => y.champion).length : 0;
  const totalTrades = Array.isArray(trades) ? trades.length : 0;

  // Rounds = largest draft's round count (covers dynasty startup + standard
  // redrafts uniformly). Falls back to null when no drafts are loaded.
  const maxRounds = Array.isArray(drafts) && drafts.length
    ? Math.max(0, ...drafts.map(d => d.rounds || 0))
    : null;

  // Highest single-season PPG (already computed in singleSeason.highestPPG).
  const topPpg = singleSeason?.highestPPG?.[0] || null;
  // Lowest single-game.
  const lowGame = singleGame?.lowest?.[0] || null;

  // Longest dynasty — most consecutive championships by one owner. Walk
  // championships.years sorted by year ascending; track current run + best.
  const sortedYears = Array.isArray(championships?.years)
    ? [...championships.years].sort((a, b) => Number(a.year) - Number(b.year)) : [];
  let bestStreak = null, curStreak = null;
  for (const y of sortedYears) {
    if (!y.champion) { curStreak = null; continue; }
    if (curStreak && curStreak.owner === y.champion) {
      curStreak.count++; curStreak.endYear = y.year;
    } else {
      curStreak = { owner: y.champion, count: 1, startYear: y.year, endYear: y.year };
    }
    if (!bestStreak || curStreak.count > bestStreak.count) bestStreak = { ...curStreak };
  }
  const longestDynasty = bestStreak && bestStreak.count >= 2 ? bestStreak : null;

  // Format string — what we can derive without raw league settings. Draft
  // type comes from the first draft; team count from coreOwners. Scoring
  // type is not threaded through funStats so it's omitted (could be added
  // later if RecapImport plumbs it through).
  const draftType = drafts?.[0]?.draftType;
  const formatBits = [];
  if (draftType && draftType !== 'unknown') {
    formatBits.push(draftType[0].toUpperCase() + draftType.slice(1));
  }
  if (totalOwners) formatBits.push(`${totalOwners} teams`);
  const formatString = formatBits.join(' · ') || null;

  return {
    totalSeasons,
    totalOwners,
    totalChampionships,
    totalTrades,
    rounds: maxRounds,
    topPpg, // { owner, year, ppg, w, l } or null
    lowGame, // { owner, year, week, pts, opp, oppPts, won } or null
    longestDynasty, // { owner, count, startYear, endYear } or null
    formatString,
  };
}

// Per-draft best/worst-pick rollups + the all-drafts ownerDraftHistory and
// teamMVPs. Operates on drafts AFTER the adapter has annotated per-pick
// values; pre-coverage drafts pass through with just the raw picks.
function computeDraftAnalysis(drafts) {
  if (!Array.isArray(drafts) || !drafts.length) return [];
  return drafts.map(d => {
    if (d.statsCoverage !== 'full') return d;
    // Best/worst picks: top 5 by valueOverExpected (desc/asc). Filter to
    // picks that actually have a vae score — skips rookies who didn't play,
    // keepers, etc.
    const scored = d.picks.filter(p => p.valueOverExpected != null);
    const bestPicks = [...scored].sort((a, b) => b.valueOverExpected - a.valueOverExpected).slice(0, 5);
    const worstPicks = [...scored].sort((a, b) => a.valueOverExpected - b.valueOverExpected).slice(0, 5);
    // Per-owner summary — totalVAE + their best + their worst.
    const perOwnerSummary = {};
    for (const p of scored) {
      if (!perOwnerSummary[p.owner]) {
        perOwnerSummary[p.owner] = { totalValueOverExpected: 0, bestPick: null, worstPick: null };
      }
      const e = perOwnerSummary[p.owner];
      e.totalValueOverExpected += p.valueOverExpected;
      if (!e.bestPick || p.valueOverExpected > e.bestPick.valueOverExpected) e.bestPick = p;
      if (!e.worstPick || p.valueOverExpected < e.worstPick.valueOverExpected) e.worstPick = p;
    }
    return { ...d, bestPicks, worstPicks, perOwnerSummary };
  });
}

function computeDraftMeta(drafts) {
  if (!Array.isArray(drafts) || !drafts.length) return { ownerDraftHistory: {}, teamMVPs: {} };
  const full = drafts.filter(d => d.statsCoverage === 'full');
  if (!full.length) return { ownerDraftHistory: {}, teamMVPs: {} };
  // ownerDraftHistory — sum of per-pick vae across every covered draft.
  const ownerDraftHistory = {};
  for (const d of full) {
    for (const p of d.picks) {
      if (p.valueOverExpected == null) continue;
      if (!ownerDraftHistory[p.owner]) {
        ownerDraftHistory[p.owner] = {
          totalValueOverExpected: 0, draftsCount: new Set(),
          bestEverPick: null, worstEverPick: null,
        };
      }
      const e = ownerDraftHistory[p.owner];
      e.totalValueOverExpected += p.valueOverExpected;
      e.draftsCount.add(d.year);
      const enriched = { ...p, year: d.year };
      if (!e.bestEverPick || p.valueOverExpected > e.bestEverPick.valueOverExpected) e.bestEverPick = enriched;
      if (!e.worstEverPick || p.valueOverExpected < e.worstEverPick.valueOverExpected) e.worstEverPick = enriched;
    }
  }
  // Materialize draftsCount as a number, compute avgVAE.
  for (const o in ownerDraftHistory) {
    const e = ownerDraftHistory[o];
    e.draftsCount = e.draftsCount.size;
    e.avgValueOverExpected = e.draftsCount ? e.totalValueOverExpected / e.draftsCount : 0;
  }
  // teamMVPs — per-year, per-owner, the highest-totalPoints drafted player.
  // "Carried the team to a championship" style narrative input. Skips
  // pre-coverage drafts. Doesn't track trades (kept picks count for the
  // drafting owner per spec section 6).
  const teamMVPs = {};
  for (const d of full) {
    teamMVPs[d.year] = {};
    const byOwner = {};
    for (const p of d.picks) {
      if (!p.seasonStats || p.seasonStats.totalPoints == null) continue;
      if (!byOwner[p.owner] || p.seasonStats.totalPoints > byOwner[p.owner].seasonStats.totalPoints) {
        byOwner[p.owner] = p;
      }
    }
    teamMVPs[d.year] = byOwner;
  }
  return { ownerDraftHistory, teamMVPs };
}

function computeChampionships(playoffs, seasonRecords, years, hintByYear) {
  const titleCounts = {};
  const mostPointsCounts = {};
  const yearsOut = [];

  const byYear = {};
  for (const m of playoffs) {
    const y = String(m.year);
    if (!byYear[y]) byYear[y] = [];
    byYear[y].push(m);
  }

  for (const y of years) {
    let champion = null, runnerUp = null, third = null, week = null;

    // Prefer adapter-supplied hint (e.g., from Sleeper winners_bracket) — it
    // resolves multi-game final weeks (championship + 3rd-place) that the
    // matchup-only derivation can't disambiguate. Hint also carries the
    // exact championship week so the A2 lineup pre-pull can find the game.
    const hint = hintByYear && hintByYear[y];
    if (hint) {
      champion = hint.champion || null;
      runnerUp = hint.runnerUp || null;
      third = hint.third || null;
      week = hint.week ?? null;
    } else {
      const games = byYear[y] || [];
      if (games.length) {
        const maxWk = Math.max(...games.map(g => g.week));
        const finalRows = games.filter(g => g.week === maxWk);
        if (finalRows.length === 2) {
          const winner = finalRows.find(r => r.won);
          const loser = finalRows.find(r => !r.won);
          if (winner && loser) { champion = winner.owner; runnerUp = loser.owner; week = maxWk; }
        }
      }
    }

    const yearSeasons = seasonRecords.filter(s => s.year === y);
    let mp = null;
    for (const s of yearSeasons) if (!mp || s.pf > mp.pf) mp = s;
    const mostPoints = mp ? mp.owner : null;

    yearsOut.push({ year: y, champion, runnerUp, third, mostPoints, week });

    if (champion) {
      if (!titleCounts[champion]) titleCounts[champion] = [];
      titleCounts[champion].push(y);
    }
    if (mostPoints) {
      if (!mostPointsCounts[mostPoints]) mostPointsCounts[mostPoints] = [];
      mostPointsCounts[mostPoints].push(y);
    }
  }

  return { years: yearsOut, titleCounts, mostPointsCounts };
}
